import { formatRelative } from "date-fns";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";

dayjs.extend(utc);

/**
 * getUTCObjectFromLocalDate:
* Ensures the date is standardized to start of the day in UTC, useful for:
* 
* Date-based filtering

* Normalizing date values in APIs

* Avoiding timezone-related bugs 
*/
export const getUTCObjectFromLocalDate = (localDate?: Date | null) => {
  // This ensures you get 00:00:00 UTC on the same calendar day
  // Converts a given localDate to a Date object representing 00:00 UTC of that day.
  // Example: if localDate = '2025-09-18T15:30:00', this will return a Date for '2025-09-18T00:00:00Z' (UTC).
  const utcStartOfDay = localDate
    ? dayjs
      .utc(
        // Format the localDate as 'YYYY-MM-DD' to strip time,
        // e.g., '2025-09-18T15:30:00' becomes '2025-09-18'
        dayjs(localDate).format("YYYY-MM-DD")
      )
      // Convert to native JS Date object at UTC midnight
      .toDate()
    : null; // If no date is provided, return null

  const responseObj = {
    utcStartOfDayIsoString: utcStartOfDay ? utcStartOfDay?.toISOString() : "",
    utcStartOfDay: utcStartOfDay,
  };

  console.log("[Debug Datehelper] Date Selected ", localDate);

  console.log("[Debug DateHelper] responseObject ", responseObj);

  return responseObj;
};

/**
 * Format a date (Date object or ISO string) to a consistent calendar date,
 * ensuring it is interpreted and formatted in UTC to avoid time zone shifts.
 *
 * Why use UTC?
 * -------------
 * - JavaScript Date objects store time in UTC internally, but Day.js formats them in local time by default.
 * - This can cause date shifts (e.g., showing the previous day) for users in time zones behind UTC.
 * - Using dayjs.utc() avoids this by formatting based on UTC, ensuring all users see the same date.
 *
 * @param inputDate - A JavaScript Date object, ISO string, or null/undefined
 * @param format - A Day.js format string (default is 'MM/DD/YYYY')
 * @returns A formatted date string like '10/03/2025' or an empty string if input is invalid
 */
export function formatUTCDate(
  inputDate: Date | string | null | undefined,
  format: string = 'MM/DD/YYYY'
): string {
  // If the input is null or undefined, return empty string to avoid errors
  if (!inputDate) return '';

  // Use dayjs.utc to ensure the input is treated in UTC and formatted consistently
  return dayjs.utc(inputDate).format(format);
}

export const formatSavedDate = (date?: Date | null) => {
  if (!date) {
    return undefined;
  }

  return formatRelative(date, new Date());
};

export function getTimeDifference(fromDate: string): string {
  const from = new Date(fromDate);
  const to = new Date();

  // Compute the remaining difference after adjusting for years and months
  let diffMs = to.getTime() - from.getTime();

  const MS_IN_SECOND = 1000;
  const MS_IN_MINUTE = MS_IN_SECOND * 60;
  const MS_IN_HOUR = MS_IN_MINUTE * 60;
  const MS_IN_DAY = MS_IN_HOUR * 24;
  const MS_IN_WEEK = MS_IN_DAY * 7;

  const weeks = Math.floor(diffMs / MS_IN_WEEK);
  diffMs %= MS_IN_WEEK;

  const days = Math.floor(diffMs / MS_IN_DAY);
  diffMs %= MS_IN_DAY;

  const hours = Math.floor(diffMs / MS_IN_HOUR);
  diffMs %= MS_IN_HOUR;

  const minutes = Math.floor(diffMs / MS_IN_MINUTE);
  diffMs %= MS_IN_MINUTE;

  const seconds = Math.floor(diffMs / MS_IN_SECOND);

  const timeParts: string[] = [];

  if (weeks > 0) timeParts.push(`${weeks} week${weeks > 1 ? "s" : ""}`);
  if (days > 0) timeParts.push(`${days} day${days > 1 ? "s" : ""}`);
  if (hours > 0) timeParts.push(`${hours} hour${hours > 1 ? "s" : ""}`);
  if (minutes > 0) timeParts.push(`${minutes} minute${minutes > 1 ? "s" : ""}`);
  if (seconds > 0) timeParts.push(`${seconds} second${seconds > 1 ? "s" : ""}`);

  return timeParts.join(", ");
}

export const formatPublishedDate = formatSavedDate;
